import { useQuery } from '@tanstack/react-query';
import { POLL, queryKeys } from '@/api/queryKeys';
import { listDeviceStatus, listDevices } from '@/api/endpoints/devices';
import { listVehicles } from '@/api/endpoints/vehicles';
import { fetchSummary, listCrashes } from '@/api/endpoints/dashboard';
import { listAlerts } from '@/api/endpoints/alerts';
import { getActiveCrashes } from '@/api/endpoints/crashes';

/**
 * The handful of reads that more than one page needs. Sharing the query keys means
 * two panels asking for the same data produce one request, and a mutation that
 * invalidates devices updates every view at once.
 */

export function useVehicles() {
  return useQuery({ queryKey: queryKeys.vehicles, queryFn: listVehicles });
}

export function useDevices() {
  return useQuery({ queryKey: queryKeys.devices, queryFn: listDevices });
}

export function useDeviceStatus(interval: number = POLL.ownerStatus) {
  return useQuery({
    queryKey: queryKeys.deviceStatus,
    queryFn: listDeviceStatus,
    refetchInterval: interval,
  });
}

export function useSummary() {
  return useQuery({
    queryKey: queryKeys.summary,
    queryFn: fetchSummary,
    refetchInterval: POLL.summary,
  });
}

export function useCrashes(limit = 50) {
  return useQuery({
    queryKey: queryKeys.crashes(limit),
    queryFn: () => listCrashes(limit),
  });
}

export function useActiveCrashes() {
  return useQuery({
    queryKey: queryKeys.activeCrashes,
    queryFn: getActiveCrashes,
    refetchInterval: POLL.activeCrash,
  });
}

export function useAlerts(limit = 100, options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: queryKeys.alerts(limit),
    queryFn: () => listAlerts(limit),
    refetchInterval: options?.refetchInterval ?? POLL.alerts,
  });
}
